import { useNavigate } from "@tanstack/react-router";
import { CopyIcon } from "lucide-react";
import { type ReactNode, useState } from "react";
import { DialogTrigger } from "react-aria-components";

import { trpc } from "@blis/trpc/react";

import { ConfirmPrompt } from "@/components/ConfirmPrompt";
import { LoadingPage } from "@/components/LoadingPage";
import { Breadcrumb } from "@/components/ui/breadcrumbs";
import { Button } from "@/components/ui/button";
import { Centered } from "@/components/ui/container";
import {
  Dialog,
  DialogBody,
  DialogCloseButton,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Description,
  FieldError,
  Input,
  Label,
  TextField,
} from "@/components/ui/field";
import { Form } from "@/components/ui/form";
import { Modal } from "@/components/ui/modal";
import { Tab, TabPanel, Tabs, TabsList } from "@/components/ui/tabs";
import { mutationResponse } from "@/utils/trpc";
import { Section } from "../components/Section";
import { ResultSpecsTable } from "../components/tests/ResultSpecsTable";
import { TestSpecDetailForm } from "../components/tests/TestSpecDetailForm";
import { TestSpecOrgsTable } from "../components/tests/TestSpecOrgsTable";
import { TestSpecPanelsTable } from "../components/tests/TestSpecPanelsTable";
import { useTabSearch } from "../hooks";

export function TestSpecsEditPage(params: { id: number }) {
  const tabsProps = useTabSearch();

  const { data, isLoading } = trpc.admin.testSpec.get.useQuery(params);

  if (isLoading) {
    return <LoadingPage />;
  }

  if (!data) {
    return <Centered>Test spec not found</Centered>;
  }

  const test = data;

  return (
    <>
      <Section.Title
        breadcrumb={
          <>
            <Breadcrumb href={{ to: "/admin/tests" }}>Tests specs</Breadcrumb>
            <Breadcrumb>
              {test.name} ({test.code})
            </Breadcrumb>
          </>
        }
        actions={
          <div className="flex items-center gap-2">
            <CloneTestSpecDialog test={test}>
              <Button variant="outline">
                <CopyIcon />
                Clone
              </Button>
            </CloneTestSpecDialog>
            <DeleteTestSpecDialog test={test}>
              <Button variant="destructive">Delete</Button>
            </DeleteTestSpecDialog>
          </div>
        }
      >
        {test.name} ({test.code})
      </Section.Title>
      <Tabs {...tabsProps}>
        <TabsList className="w-full justify-start">
          <Tab id="details">Details</Tab>
          <Tab id="results">Result specs</Tab>
          <Tab id="panels">Panels</Tab>
          <Tab id="clients">Clients</Tab>
        </TabsList>
        <TabPanel id="details">
          <TestSpecDetailForm test={test} />
        </TabPanel>
        <TabPanel id="results">
          <ResultSpecsTable testSpec={test} />
        </TabPanel>
        <TabPanel id="panels">
          <TestSpecPanelsTable testSpec={test} />
        </TabPanel>
        <TabPanel id="clients">
          <TestSpecOrgsTable testSpec={test} />
        </TabPanel>
      </Tabs>
    </>
  );
}

function DeleteTestSpecDialog({
  children,
  test,
}: {
  test: { id: number };
  children: ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const deleteSpec = trpc.admin.testSpec.delete.useMutation();
  const onConfirm = () => {
    deleteSpec.mutate(
      test,
      mutationResponse({
        errorTitle: "Error deleting test spec",
        successMessage: "Test spec deleted",
        onSuccess() {
          setOpen(false);
          navigate({ to: "/admin/tests" });
        },
      }),
    );
  };
  return (
    <ConfirmPrompt
      open={open}
      onOpenChange={setOpen}
      danger
      title="Delete test spec"
      message="Are you sure you want to delete this test spec?"
      onConfirm={onConfirm}
      loading={deleteSpec.isPending}
    >
      {children}
    </ConfirmPrompt>
  );
}

function CloneTestSpecDialog({
  children,
  test,
}: {
  test: { id: number; name: string };
  children: ReactNode;
}) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const cloneMutation = trpc.admin.testSpec.clone.useMutation(
    mutationResponse({
      errorTitle: "Error cloning test spec",
      successMessage: "Test spec cloned",
      onSuccess(data) {
        navigate({ to: "/admin/tests/$id/edit", params: { id: data.id } });
      },
    }),
  );

  return (
    <DialogTrigger isOpen={open} onOpenChange={setOpen}>
      {children}
      <Modal>
        <Dialog>
          <DialogHeader>
            <DialogTitle>Clone test spec</DialogTitle>
          </DialogHeader>
          <DialogBody>
            <Description>
              Clone this test spec and create a new one with a new code.
            </Description>
            <Form
              id="clone-form"
              onSubmit={(e) => {
                e.preventDefault();
                const formData = new FormData(e.target as HTMLFormElement);
                const code = formData.get("code") as string;
                const name = formData.get("name") as string;
                cloneMutation.mutate({ id: test.id, code, name });
              }}
              className="flex flex-col gap-2"
            >
              <TextField
                isRequired
                name="name"
                defaultValue={`${test.name} (clone)`}
              >
                <Label>Name</Label>
                <Input />
                <FieldError />
              </TextField>
              <TextField isRequired name="code">
                <Label>New code</Label>
                <Input />
                <FieldError />
              </TextField>
            </Form>
          </DialogBody>
          <DialogFooter>
            <Button
              form="clone-form"
              type="submit"
              isPending={cloneMutation.isPending}
              isDisabled={cloneMutation.isPending}
            >
              Clone
            </Button>
            <DialogCloseButton>Cancel</DialogCloseButton>
          </DialogFooter>
        </Dialog>
      </Modal>
    </DialogTrigger>
  );
}
